import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  accountsEnabled,
  AdultAuthRequestError,
  request,
} from './AdultAuthPanel';
import './AdultAccountPage.css';
import { SpaceIllustration } from './AccountsVisuals';

export const INVALID_RECOVERY_LINK_MESSAGE = 'El enlace de recuperación no es válido o expiró. Solicita uno nuevo.';
export const RECOVERY_COMPLETE_MESSAGE = 'Tu contraseña fue actualizada. Ya puedes iniciar sesión.';
export const PASSWORD_REJECTED_MESSAGE = 'La nueva contraseña no cumple con los requisitos de seguridad. Intenta con otra.';

export function readRecoverySession(hash) {
  const params = new URLSearchParams((hash || '').replace(/^#/, ''));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  if (!accessToken || !refreshToken || params.get('type') !== 'recovery') {
    return null;
  }

  return { access_token: accessToken, refresh_token: refreshToken };
}

export function getInitialRecoveryCallbackState(hash) {
  const recoverySession = readRecoverySession(hash);
  return {
    recoverySession,
    invalidLinkMessage: recoverySession ? '' : INVALID_RECOVERY_LINK_MESSAGE,
  };
}

export function recoveryErrorMessage(error) {
  if (error instanceof AdultAuthRequestError && error.status === 401) {
    return INVALID_RECOVERY_LINK_MESSAGE;
  }
  if (error instanceof AdultAuthRequestError && error.status === 400) {
    return PASSWORD_REJECTED_MESSAGE;
  }
  return 'No fue posible actualizar la contraseña. Inténtalo de nuevo.';
}

export default function AdultPasswordResetPage() {
  const [initialRecoveryCallback] = useState(() => (
    getInitialRecoveryCallbackState(window.location.hash)
  ));
  const [recoverySession, setRecoverySession] = useState(
    () => initialRecoveryCallback.recoverySession,
  );
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const recoveryFragmentHandled = useRef(false);

  useEffect(() => {
    if (!accountsEnabled || recoveryFragmentHandled.current) return;

    recoveryFragmentHandled.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);
    if (initialRecoveryCallback.invalidLinkMessage) {
      setNotice(initialRecoveryCallback.invalidLinkMessage);
    }
  }, [initialRecoveryCallback]);

  if (!accountsEnabled) return null;

  async function submit(event) {
    event.preventDefault();
    if (!recoverySession) return;

    const form = new FormData(event.currentTarget);
    const password = String(form.get('password') || '');
    const confirmation = String(form.get('confirmation') || '');

    if (password !== confirmation) {
      setNotice('Las contraseñas no coinciden.');
      return;
    }

    setBusy(true);
    setNotice('');
    try {
      await request('/auth/password-recovery/complete', {
        method: 'POST',
        body: JSON.stringify({ ...recoverySession, password }),
      });
      setRecoverySession(null);
      setNotice(RECOVERY_COMPLETE_MESSAGE);
    } catch (error) {
      setNotice(recoveryErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="tmag-account tmag-account--reset">
      <section className="tmag-account__panel" aria-labelledby="adult-password-reset-title">
        <Link className="tmag-account__reset-back" to="/account">← MI CUENTA</Link>
        <header className="tmag-account__hero"><div><p className="tmag-account__eyebrow">DE VUELTA A TU GALAXIA</p><h1 id="adult-password-reset-title">Tu nueva contraseña</h1><p>Elige una contraseña segura de al menos 8 caracteres.</p></div><SpaceIllustration compact /></header>
        {recoverySession ? (
          <form onSubmit={submit}>
            <label>
              Nueva contraseña
              <input name="password" type="password" autoComplete="new-password" minLength="8" maxLength="128" required />
            </label>
            <label>
              Confirmar contraseña
              <input name="confirmation" type="password" autoComplete="new-password" minLength="8" maxLength="128" required />
            </label>
            <button className="tmag-account__reset-action" type="submit" disabled={busy}>
              ACTUALIZAR CONTRASEÑA
            </button>
          </form>
        ) : null}
        {notice && <p className="tmag-account__notice" role="status">{notice}</p>}
      </section>
    </main>
  );
}
